/* Copyright (C) Okahu Inc 2023-2024. All rights reserved.*/

import * as go from 'gojs';

import { highlightPath } from '../../../helpers/graph';
import { NODE_COMMON_STYLES, NODE_STYLES } from '../graph-config';
import { createNodeToolTip, GoMake } from './shared';

/**
 * Tool node template (circle - orange/amber) with wrench icon and label below
 */
export function createToolNodeTemplate($: GoMake): go.Node {
  return $(
    go.Node,
    'Vertical',
    {
      selectionAdorned: false,
      layoutConditions: go.LayoutConditions.Added | go.LayoutConditions.Removed,
      selectionChanged: (node: go.Part) => {
        const n = node as go.Node;
        const circle = n.findObject('TOOL_CIRCLE') as go.Shape;
        if (n.isSelected) {
          if (circle) {
            circle.stroke = NODE_STYLES.tool.selected.stroke;
            circle.strokeWidth = NODE_STYLES.tool.selected.strokeWidth;
          }
          n.isShadowed = true;
          n.shadowColor = NODE_STYLES.tool.selected.shadowColor;
          n.shadowBlur = NODE_STYLES.tool.selected.shadowBlur;
          n.shadowOffset = new go.Point(NODE_STYLES.tool.selected.shadowOffset.x, NODE_STYLES.tool.selected.shadowOffset.y);
        } else {
          const hasError = n.data?.hasError;
          if (circle) {
            circle.stroke = hasError ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.tool.stroke;
            circle.strokeWidth = hasError ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.tool.strokeWidth;
          }
          n.isShadowed = false;
        }
      },
      mouseEnter: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        highlightPath(node, null, true, true);
        if (node.isSelected) return;
        const circle = node.findObject('TOOL_CIRCLE') as go.Shape;
        if (circle) {
          circle.stroke = NODE_STYLES.tool.hover.stroke;
          circle.strokeWidth = NODE_STYLES.tool.hover.strokeWidth;
        }
        node.isShadowed = true;
        node.shadowColor = NODE_STYLES.tool.hover.shadowColor;
        node.shadowBlur = NODE_STYLES.tool.hover.shadowBlur;
        node.shadowOffset = new go.Point(NODE_STYLES.tool.hover.shadowOffset.x, NODE_STYLES.tool.hover.shadowOffset.y);
      },
      mouseLeave: (_e: go.InputEvent, obj: go.GraphObject) => {
        const node = obj as go.Node;
        highlightPath(node, null, false, true);
        if (node.isSelected) return;
        const circle = node.findObject('TOOL_CIRCLE') as go.Shape;
        const hasError = node.data?.hasError;
        if (circle) {
          circle.stroke = hasError ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.tool.stroke;
          circle.strokeWidth = hasError ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.tool.strokeWidth;
        }
        node.isShadowed = false;
      },
      toolTip: createNodeToolTip($),
    },
    // Circle with icon inside
    $(
      go.Panel,
      'Spot',
      // Circle shape as background
      $(go.Shape, NODE_STYLES.tool.shape, {
        name: 'TOOL_CIRCLE',
        desiredSize: NODE_STYLES.tool.size,
        fill: NODE_STYLES.tool.fill,
        stroke: NODE_STYLES.tool.stroke,
        strokeWidth: NODE_STYLES.tool.strokeWidth,
      },
        new go.Binding('stroke', 'hasError', (err: boolean) =>
          err ? NODE_COMMON_STYLES.error.stroke : NODE_STYLES.tool.stroke
        ),
        new go.Binding('strokeWidth', 'hasError', (err: boolean) =>
          err ? NODE_COMMON_STYLES.error.strokeWidth : NODE_STYLES.tool.strokeWidth
        )
      ),
      // Logo/icon image (centered inside the circle)
      $(go.Picture, {
        desiredSize: NODE_COMMON_STYLES.iconSize,
      }, new go.Binding('source', 'source')),
      // Error badge (shown only when hasError is true)
      $(go.Picture, {
        desiredSize: NODE_COMMON_STYLES.error.badgeSize,
        alignment: NODE_COMMON_STYLES.error.badgeAlignment,
        visible: false,
        source: '',
      },
        new go.Binding('visible', 'hasError'),
        new go.Binding('source', 'errorBadge')
      ),
      // Left port (transparent)
      $(go.Shape, 'Circle', {
        width: 2,
        height: 2,
        fill: 'transparent',
        stroke: 'transparent',
        portId: 'left',
        alignment: go.Spot.Left,
        fromLinkable: true,
        toLinkable: true,
      }),
      // Right port (transparent)
      $(go.Shape, 'Circle', {
        width: 2,
        height: 2,
        fill: 'transparent',
        stroke: 'transparent',
        portId: 'right',
        alignment: go.Spot.Right,
        fromLinkable: true,
        toLinkable: true,
      })
    ),
    // Label below the circle
    $(
      go.TextBlock,
      {
        font: NODE_COMMON_STYLES.labelFont,
        stroke: NODE_STYLES.tool.textStroke,
        textAlign: 'center',
        margin: NODE_COMMON_STYLES.labelMargin,
      },
      new go.Binding('text', 'name')
    )
  );
}
